import { useState } from 'react'
import { Link } from 'react-router-dom'
import reportingApi from '../../services/reportingApi'
import { T, page, usePoll, Empty, Stat, MatchRow, SectionTitle, ScreenButton } from './ui'
import Scorecard from './Scorecard'

/**
 * The championship as a whole — for whoever is running all ten cities, which is
 * a handful of people. Everyone at a venue wants their own city instead, so
 * this sits at the end of the navigation rather than the front.
 */
const Overview = () => {
  const [openMatch, setOpenMatch] = useState(null)
  const { data, error } = usePoll(() => reportingApi.live(), 20000, [])

  if (error) return <div style={page}><Empty>{error}</Empty></div>
  if (!data) return <div style={page}><Empty>Loading…</Empty></div>

  return (
    <div style={page}>
      <h2 style={{ margin: '0 0 4px', fontSize: 24 }}>All cities</h2>
      <p style={{ margin: '0 0 20px', color: T.muted, fontSize: 14 }}>
        The whole championship at once. For a venue, open its city instead.
      </p>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))',
        gap: 12, marginBottom: 26,
      }}>
        <Stat value={data.totals.live} label="In progress" tone="live" />
        <Stat value={data.totals.played.toLocaleString('en-IN')} label="Played" />
        <Stat value={data.totals.remaining.toLocaleString('en-IN')} label="To play" />
        <Stat value={data.totals.matches.toLocaleString('en-IN')} label="Total" />
      </div>

      <section style={{ marginBottom: 30 }}>
        <SectionTitle>By city</SectionTitle>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 520 }}>
            <thead>
              <tr>{['City', 'Live', 'Played', 'Total', ''].map((h) => (
                <th key={h} style={th}>{h}</th>))}</tr>
            </thead>
            <tbody>
              {data.by_city.map((c) => (
                <tr key={c.city}>
                  <td style={{ ...td, fontWeight: 700 }}>{c.city}</td>
                  <td style={td}>
                    {c.live > 0
                      ? <span style={{ color: T.live, fontWeight: 800 }}>{c.live}</span>
                      : <span style={{ color: T.faint }}>—</span>}
                  </td>
                  <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>
                    {c.played.toLocaleString('en-IN')}
                  </td>
                  <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>
                    {c.matches.toLocaleString('en-IN')}
                  </td>
                  <td style={td}>
                    <Link to={`/board/city/${encodeURIComponent(c.city)}`} style={{
                      color: T.accent, fontWeight: 700, fontSize: 12.5, textDecoration: 'none',
                    }}>Open →</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <Section title="On court now" rows={data.live} onOpen={setOpenMatch}
               empty="No match is being scored right now." />
      <Section title="Just finished" rows={data.recent} onOpen={setOpenMatch}
               empty="Results appear here as they land." />

      {openMatch && <Scorecard fixtureId={openMatch} onClose={() => setOpenMatch(null)} />}
    </div>
  )
}

const Section = ({ title, rows = [], onOpen, empty }) => (
  <section style={{ marginBottom: 28 }}>
    <SectionTitle>{title}</SectionTitle>
    {rows.length === 0 ? <Empty>{empty}</Empty> : (
      <div style={{ display: 'grid', gap: 8 }}>
        {rows.map((m) => (
          <MatchRow key={m.id}
                    m={{ ...m, category: [m.sport_label, m.city, m.category].filter(Boolean).join(' · ') }}
                    onOpen={onOpen}
                    action={m.status === 'live'
                      ? <ScreenButton city={m.city} sport={m.sport} court={m.court} />
                      : null} />
        ))}
      </div>
    )}
  </section>
)

const th = {
  textAlign: 'left', padding: '9px 12px', borderBottom: `2px solid #dde3ea`, fontSize: 11,
  textTransform: 'uppercase', letterSpacing: '.08em', color: T.muted, fontWeight: 800,
}
const td = { padding: '9px 12px', borderBottom: `1px solid #eef1f5`, fontSize: 13.5 }

export default Overview
